/**
 * HDL New Purchase Section JavaScript
 * - Calculates bundle total and discounted price dynamically based on customizer percentage
 * - Adds all selected bundle products + optional freebie to the Shopify cart in a single request
 * - Opens and renders the cart drawer upon addition
 */

(function () {
  'use strict';

  function initHdlNewPurchaseSection(section) {
    if (!section || section.dataset.hdlInitialized === 'true') return;
    section.dataset.hdlInitialized = 'true';

    const discountPercentage = parseFloat(section.dataset.discountPercentage) || 15;
    const comparePriceEl = section.querySelector('.hdl-new-purchase__compare-price');
    const discountedPriceEl = section.querySelector('.hdl-new-purchase__discounted-price');
    const productItems = Array.from(section.querySelectorAll('.hdl-new-purchase__product-item[data-price]'));
    const addToCartBtn = section.querySelector('.hdl-new-purchase__btn');
    const errorMsgEl = section.querySelector('.hdl-new-purchase__error-message');

    // Dynamic price calculation
    function calculateAndRenderPricing() {
      // If manual price override is set via data-manual-price, skip dynamic calculation
      if (section.dataset.hasManualPrice === 'true') return;

      if (productItems.length === 0) return;

      let totalOriginalCents = 0;
      let validCount = 0;

      productItems.forEach((item) => {
        const price = parseFloat(item.dataset.price);
        if (!isNaN(price) && price > 0) {
          totalOriginalCents += price;
          validCount++;
        }
      });

      if (validCount === 0) return;

      const discountedCents = Math.round(totalOriginalCents * (1 - discountPercentage / 100));

      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoney(totalOriginalCents);
      }
      if (discountedPriceEl) {
        discountedPriceEl.textContent = formatMoney(discountedCents);
      }
    }

    function formatMoney(cents) {
      if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.moneyFormat || 'CHF {{amount}}');
      }

      const amount = (cents / 100).toFixed(2);
      const isClean = cents % 100 === 0;
      const formattedAmount = isClean ? `${cents / 100}.–` : amount;
      const currency = window.Shopify?.currency?.active || 'CHF';
      return `${currency} ${formattedAmount}`;
    }

    // Add to Cart Click Handler
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', async function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (addToCartBtn.classList.contains('loading')) return;

        // Collect all bundle product variant IDs
        const itemsToAdd = [];
        const isGerman = window.location.pathname.includes('/de');
        const discountPropName = isGerman ? 'Glückwunsch!' : 'Congrats!';
        const discountPropVal = isGerman
          ? `${discountPercentage}% Rabatt angewendet`
          : `${discountPercentage}% discount applied`;

        productItems.forEach((item) => {
          const variantId = item.dataset.variantId;
          if (variantId && Number(variantId)) {
            itemsToAdd.push({
              id: Number(variantId),
              quantity: 1,
              properties: {
                [discountPropName]: discountPropVal,
                _bundle: 'hdl_founders_collection'
              }
            });
          }
        });

        if (itemsToAdd.length === 0) {
          console.warn('HDL New Purchase: No products available to add.');
          return;
        }

        // Set Loading State
        if (errorMsgEl) errorMsgEl.style.display = 'none';
        addToCartBtn.classList.add('loading');
        addToCartBtn.setAttribute('aria-disabled', 'true');
        if (typeof showCartGlobalLoader === 'function') showCartGlobalLoader();

        const cartDrawer = document.querySelector('cart-drawer');
        let sectionsArr = [];
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          sectionsArr = cartDrawer.getSectionsToRender().map((s) => s.id);
        }

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: itemsToAdd,
              sections: sectionsArr,
              sections_url: window.location.pathname
            })
          }).then((res) => res.json());

          // Check if response has errors
          if (response.status) {
            console.error('HDL Add to Cart Error:', response.description || response.message);
            if (errorMsgEl) {
              errorMsgEl.textContent = response.description || response.message || 'Error adding products to cart';
              errorMsgEl.style.display = 'block';
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'hdl-new-purchase',
                errors: response.description,
                message: response.message
              });
            }
            return;
          }

          // Trigger Theme Custom Events
          if (typeof $ !== 'undefined') {
            $(document).trigger('product-form-responsed', [response]);
          }
          document.dispatchEvent(new Event('product-form-responsed'));

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'hdl-new-purchase',
              response: response
            });
          }

          // Render Cart Drawer
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(response);
          } else if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          // Animate Progress Bar
          if (typeof animateCartDrawerProgressBar === 'function') {
            animateCartDrawerProgressBar();
          }

          // Set order note language tag (DE / EN)
          const orderNote = window.location.pathname.includes('/de') ? 'DE' : 'EN';
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: orderNote })
          });

        } catch (err) {
          console.error('HDL ATC Exception:', err);
          if (errorMsgEl) {
            errorMsgEl.textContent = 'Could not add items to cart. Please try again.';
            errorMsgEl.style.display = 'block';
          }
        } finally {
          addToCartBtn.classList.remove('loading');
          addToCartBtn.removeAttribute('aria-disabled');
          if (typeof hideCartGlobalLoader === 'function') hideCartGlobalLoader();
        }
      });
    }

    // Initial pricing calculation
    calculateAndRenderPricing();
  }

  function initAllHdlSections() {
    document.querySelectorAll('.hdl-new-purchase-section').forEach(initHdlNewPurchaseSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllHdlSections);
  } else {
    initAllHdlSections();
  }

  // Support Shopify section rendering in Theme Editor
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector('.hdl-new-purchase-section');
    if (section) initHdlNewPurchaseSection(section);
  });
})();

